"use client";

import * as React from "react";
import { AnimatePresence, motion } from "framer-motion";
import { cn } from "../../utils/utils";

export function ExpandableTabs({
  tabs,
  className,
  activeColor = "text-[#D4AF37]",
  onChange,
}) {
  const [selected, setSelected] = React.useState(null);
  const [hovered, setHovered] = React.useState(null);

  const handleSelect = (index) => {
    setSelected(index);
    onChange?.(index);
  };

  return (
    <nav
      className={cn(
        "fixed z-50 left-1/2 -translate-x-1/2 top-5",
        "flex items-center",
        "rounded-full",
        "border border-[#D4AF37]/45",
        "bg-[#080808]/85",
        "backdrop-blur-xl",
        "shadow-[0_0_30px_rgba(212,175,55,0.08)]",
        "px-1.5 py-1.5",
        "max-w-[calc(100vw-24px)]",
        className
      )}
      aria-label="Main navigation"
    >
      <div
        className={cn(
          "flex items-center gap-0.5",
          "overflow-x-auto",
          "scrollbar-none",
          "max-w-full"
        )}
      >
        {tabs.map((tab, index) => {
          const Icon = tab.icon;

          const isSelected = selected === index;
          const isHovered = hovered === index;

          // Selected item stays expanded.
          // Hovered item expands temporarily.
          const isExpanded = isSelected || isHovered;

          return (
            <React.Fragment key={tab.title}>
              <motion.a
                href={tab.href}
                onClick={() => handleSelect(index)}
                onMouseEnter={() => setHovered(index)}
                onMouseLeave={() => setHovered(null)}
                initial={false}
                animate={{
                  gap: isExpanded ? 8 : 0,
                  paddingLeft: isExpanded ? 14 : 10,
                  paddingRight: isExpanded ? 14 : 10,
                }}
                transition={{
                  type: "spring",
                  stiffness: 400,
                  damping: 30,
                }}
                className={cn(
                  "relative flex shrink-0 items-center justify-center",
                  "h-9 rounded-full",
                  "text-gray-400",
                  "transition-colors duration-200",
                  "hover:text-white",
                  "focus:outline-none focus-visible:ring-1",
                  "focus-visible:ring-[#D4AF37]",
                  isSelected && activeColor
                )}
                aria-label={tab.title}
                aria-current={isSelected ? "page" : undefined}
              >
                <motion.span
                  animate={{
                    scale: isSelected ? 1.05 : 1,
                  }}
                  transition={{
                    type: "spring",
                    stiffness: 400,
                    damping: 25,
                  }}
                >
                  <Icon
                    size={17}
                    strokeWidth={1.6}
                    className="shrink-0"
                  />
                </motion.span>

                <AnimatePresence initial={false}>
                  {isExpanded && (
                    <motion.span
                      initial={{
                        width: 0,
                        opacity: 0,
                      }}
                      animate={{
                        width: "auto",
                        opacity: 1,
                      }}
                      exit={{
                        width: 0,
                        opacity: 0,
                      }}
                      transition={{
                        duration: 0.2,
                        ease: "easeOut",
                      }}
                      className={cn(
                        "overflow-hidden whitespace-nowrap",
                        "text-[10px] font-medium",
                        "tracking-[0.12em] uppercase"
                      )}
                      style={{ paddingLeft: "4px" }}
                    >
                      {tab.title}
                    </motion.span>
                  )}
                </AnimatePresence>
              </motion.a>

              {index < tabs.length - 1 && (
                <div
                  className="h-5 w-px shrink-0 bg-white/10"
                  aria-hidden="true"
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </nav>
  );
}
